import React, { useState, useEffect, useMemo } from "react";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import BASE_URL from "../utils/Urls";


const ROLE_LABEL = { work_doer: "WD", work_giver: "WG" };

const formatJoined = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const CompanyUnrootedPopup = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(20);
  const [total, setTotal] = useState(0);
  const [promoters, setPromoters] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/admin/company-unrooted`);
        if (!alive) return;
        setWindowDays(Number(res.data?.window_days) || 20);
        setTotal(Number(res.data?.total) || 0);
        setPromoters(Array.isArray(res.data?.promoters) ? res.data.promoters : []);
      } catch (err) {
        console.error("❌ Failed to load company-unrooted accounts", err);
        if (alive) setPromoters([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return promoters;
    return promoters
      .map((p) => ({
        ...p,
        accounts: p.accounts.filter(
          (a) =>
            (a.full_name || "").toLowerCase().includes(q) ||
            (a.phone || "").toLowerCase().includes(q)
        ),
      }))
      .filter((p) => p.accounts.length > 0);
  }, [promoters, search]);

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-start z-50 overflow-y-auto py-8">
      <div
        className="bg-white rounded-lg shadow-lg flex flex-col relative animate-slideUp my-auto"
        style={{ width: "560px", maxHeight: "85vh" }}
      >
        <button
          onClick={() => onClose?.()}
          className="absolute top-3 right-5 text-red-600 hover:text-red-800 z-10"
        >
          <IoClose className="text-2xl" />
        </button>

        <div className="px-6 pt-5 pb-3">
          <h2 className="text-center font-bold text-xl text-gray-800">Company unrooted accounts</h2>
          <p className="text-center text-xs text-gray-400">
            Referred users not verified within signup&nbsp;+&nbsp;{windowDays} days · {total} total
          </p>
        </div>

        <div className="px-6 pb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone…"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
          />
        </div>

        <div className="px-6 pb-5 overflow-y-auto" style={{ flex: 1 }}>
          {loading ? (
            <div className="text-center text-gray-400 py-8">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-400 py-8">No company unrooted accounts.</div>
          ) : (
            filtered.map((p) => (
              <div key={p.user_id} className="mb-4">
                <div className="flex items-center justify-between bg-gray-100 rounded-t px-3 py-2">
                  <div className="font-bold text-sm text-gray-800">
                    {p.employer_name || p.main_employee_code}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {p.main_employee_code} · {p.group}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-red-500">{p.accounts.length}</span>
                </div>
                <ul className="border border-t-0 border-gray-200 rounded-b divide-y divide-gray-100">
                  {p.accounts.map((a) => (
                    <li key={a._id} className="flex items-center justify-between px-3 py-2">
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {a.full_name || "—"}
                          {Array.isArray(a.roles) && a.roles.length > 0 && (
                            <span className="ml-2 text-[10px] text-gray-500">
                              {a.roles.map((r) => ROLE_LABEL[r] || r).join(", ")}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Joined {formatJoined(a.registered_at)}</div>
                      </div>
                      {a.phone ? (
                        <a
                          href={`tel:${a.phone}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {a.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyUnrootedPopup;
